import React from 'react';
import { api, formatINR } from '../../../lib/store';
import { formatSizeInput } from '../../../lib/utils';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Badge } from '../../ui/badge';
import { Separator } from '../../ui/separator';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, ChevronLeft, ChevronRight, Image as ImageIcon, X, Wrench, Upload, Package, Zap, Download } from 'lucide-react';
import { logger } from '../../../lib/logger';
import {
  CATEGORIES,
  RATE_TYPES,
  THICKNESS_BULK_CATEGORIES,
  WEIGHT_BULK_CATEGORIES,
  PLYWOOD_THICKNESSES,
  BLOCKBOARD_THICKNESSES,
  MDF_THICKNESSES,
  DOORS_THICKNESSES,
  WPCPVC_THICKNESSES,
  ACP_THICKNESSES,
  ADHESIVE_WEIGHTS,
  PLYWOOD_SIZES,
  DOOR_SIZES,
  DOOR_SIZES_WITH_SQFT,
  emptyProduct,
} from '../constants';

// Extracted from ProductManagement.js (R2). Receives parent state/handlers as props.
export function CategoryManageDialog(props) {
  const {
    cancelDelete,
    categoryPassword,
    categoryToDelete,
    customCategories,
    deleteConfirmStep,
    deleteCustomCategory,
    newCategoryName,
    saveCustomCategory,
    setCategoryPassword,
    setCategoryToDelete,
    setDeleteConfirmStep,
    setNewCategoryName,
    setShowCategoryDialog,
    showCategoryDialog,
  } = props;
  return (
    <>
      {/* Add Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={(open) => {
        setShowCategoryDialog(open);
        if (!open) {
          setNewCategoryName('');
          setCategoryPassword('');
          setCategoryToDelete(null);
          setDeleteConfirmStep(0);
        }
      }}>
        <DialogContent className="max-w-sm" data-testid="add-category-dialog">
          <DialogHeader>
            <DialogTitle className="text-heda-ink">Manage Categories</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">New Category Name</Label>
              <Input
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="e.g., Glass, Tiles, Flooring"
                className="rounded-xl mt-1 bg-heda-cream border-heda-line"
                data-testid="new-category-input"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Password (Required)</Label>
              <Input
                type="password"
                value={categoryPassword}
                onChange={(e) => setCategoryPassword(e.target.value)}
                placeholder="Enter password"
                className="rounded-xl mt-1 bg-heda-cream border-heda-line"
                onKeyDown={(e) => e.key === 'Enter' && saveCustomCategory()}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => { setShowCategoryDialog(false); setNewCategoryName(''); setCategoryPassword(''); }}
                className="rounded-xl border-heda-line bg-white text-heda-ink shadow-none hover:bg-heda-cream/60 flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={saveCustomCategory}
                className="rounded-xl bg-heda-ink text-white shadow-none hover:bg-black flex-1"
                data-testid="save-category-btn"
              >
                Add Category
              </Button>
            </div>
            
            {/* Custom categories list with delete */}
            {customCategories.length > 0 && (
              <div className="pt-3 border-t border-heda-line">
                <p className="text-xs text-heda-mute mb-2">Custom categories added:</p>
                <div className="space-y-2">
                  {customCategories.map(cat => (
                    <div key={cat} className="flex items-center justify-between bg-heda-cream px-3 py-2 rounded-xl">
                      <span className="text-sm">{cat}</span>
                      {categoryToDelete === cat ? (
                        <div className="flex items-center gap-2">
                          {deleteConfirmStep === 1 && (
                            <>
                              <span className="text-xs text-red-700">Delete?</span>
                              <Button size="sm" variant="destructive" className="rounded-xl bg-red-700 text-white shadow-none hover:bg-red-800 h-8 text-xs" onClick={() => deleteCustomCategory(cat)}>Yes</Button>
                              <Button size="sm" variant="outline" className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold h-6 text-xs" onClick={cancelDelete}>No</Button>
                            </>
                          )}
                          {deleteConfirmStep === 2 && (
                            <>
                              <Input
                                type="password"
                                value={categoryPassword}
                                onChange={(e) => setCategoryPassword(e.target.value)}
                                placeholder="Password"
                                className="rounded-xl w-24 h-8 text-base md:text-xs bg-heda-cream/50"
                                onKeyDown={(e) => e.key === 'Enter' && deleteCustomCategory(cat)}
                              />
                              <Button size="sm" variant="destructive" className="rounded-xl bg-red-700 text-white shadow-none hover:bg-red-800 h-8 text-xs" onClick={() => deleteCustomCategory(cat)}>Confirm</Button>
                              <Button size="sm" variant="outline" className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold h-6 text-xs" onClick={cancelDelete}>Cancel</Button>
                            </>
                          )}
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="rounded-full hover:bg-heda-cream h-8 text-xs text-red-700 hover:text-red-700 hover:bg-red-50"
                          onClick={() => deleteCustomCategory(cat)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
